import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Clock, Footprints, Images, MapPin, Sparkles } from "lucide-react";
import { api, fileUrl } from "@/lib/api";
import { useCommunity } from "@/context/CommunityContext";
import TopBar from "@/components/TopBar";
import IdeaBlock from "@/components/IdeaBlock";

export default function Walks() {
  const { community, slug } = useCommunity();
  const [walks, setWalks] = useState([]);

  useEffect(() => {
    if (!slug) return;
    api.get(`/communities/${slug}/walks`).then(({ data }) => setWalks(data || [])).catch(() => setWalks([]));
  }, [slug]);

  return (
    <div data-testid="walks-page">
      <TopBar back="/" title="Erlebnisse" />
      <div className="mx-auto max-w-2xl px-5 py-8">
        <p className="eyebrow">St. Ruprecht an der Raab</p>
        <h1 className="mt-1 text-4xl">SPUREN entdecken</h1>
        <p className="mt-2 text-sm text-[var(--ink-soft)]">
          Wähle zwischen einem geführten Weg durch den Ort und einem freien Museumserlebnis im Kunstpark.
        </p>

        <div className="mt-8 space-y-4">
          {walks.length === 0 ? (
            <p className="rounded-2xl border bg-white p-6 text-sm text-[var(--ink-soft)]" data-testid="walks-empty">
              Es sind noch keine Wege veröffentlicht.
            </p>
          ) : null}
          {walks.map((walk, index) => {
            const openMuseum = walk.experience_mode === "OPEN_COLLECTION";
            const Icon = openMuseum ? Images : Footprints;
            return (
              <Link
                key={walk.id}
                to={`/wege/${walk.slug || walk.id}`}
                className="card-lift group block overflow-hidden rounded-3xl border bg-white rise"
                style={{ animationDelay: `${index * 60}ms` }}
                data-testid={`walk-card-${walk.slug || walk.id}`}
              >
                <div className="relative h-52 w-full overflow-hidden bg-[var(--brand-soft)] grain">
                  {walk.hero_image ? (
                    <img src={fileUrl(walk.hero_image)} alt="" className="h-full w-full object-cover transition duration-700 group-hover:scale-[1.03]" />
                  ) : null}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/10" />
                  <div className="absolute inset-x-0 top-0 flex items-start justify-between gap-3 p-4">
                    <span className="inline-flex items-center gap-2 rounded-full bg-white/95 px-3 py-1.5 text-[10px] font-bold tracking-[0.12em] text-[var(--brand)]">
                      <Icon className="h-3.5 w-3.5" /> {openMuseum ? "FREIES MUSEUM" : "GEFÜHRTER WEG"}
                    </span>
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-[var(--brand)] px-3 py-1.5 text-[10px] font-bold text-white">
                      <Sparkles className="h-3.5 w-3.5" /> +{walk.completion_xp ?? 100} XP
                    </span>
                  </div>
                  {walk.status === "COMING_SOON" ? (
                    <span className="absolute bottom-4 left-4 rounded-full bg-white/95 px-3 py-1 text-xs font-semibold">
                      In Vorbereitung
                    </span>
                  ) : null}
                </div>
                <div className="p-5">
                  <p className="eyebrow">{walk.collectible_number || (openMuseum ? "SR-002" : "SR-001")}</p>
                  <p className="mt-1 text-3xl">{walk.title}</p>
                  {walk.subtitle ? <p className="mt-1 text-sm text-[var(--ink-soft)]">{walk.subtitle}</p> : null}
                  <p className="mt-3 text-sm leading-relaxed">{walk.short_description}</p>
                  <div className="mt-4 flex flex-wrap gap-2 text-xs text-[var(--ink-soft)]">
                    {openMuseum ? (
                      <>
                        <span className="rounded-full bg-[var(--brand-soft)] px-3 py-1.5">{walk.artwork_count ?? 49} Kunstwerke</span>
                        <span className="rounded-full bg-[var(--brand-soft)] px-3 py-1.5">{walk.completion_target ?? 6} frei wählen</span>
                        <span className="rounded-full bg-[var(--brand-soft)] px-3 py-1.5">Lieblingswerk</span>
                      </>
                    ) : (
                      <>
                        {walk.distance_text ? (
                          <span className="inline-flex items-center gap-1.5 rounded-full bg-[var(--brand-soft)] px-3 py-1.5">
                            <MapPin className="h-3.5 w-3.5" /> {walk.distance_text}
                          </span>
                        ) : null}
                        {walk.duration_text ? (
                          <span className="inline-flex items-center gap-1.5 rounded-full bg-[var(--brand-soft)] px-3 py-1.5">
                            <Clock className="h-3.5 w-3.5" /> {walk.duration_text}
                          </span>
                        ) : null}
                      </>
                    )}
                  </div>
                  <span className="mt-5 inline-flex items-center gap-2 text-xs font-bold tracking-[0.1em] text-[var(--brand)]">
                    {openMuseum ? "KUNSTPARK ÖFFNEN" : "SPUR ÖFFNEN"}
                    <ArrowRight className="h-4 w-4 transition group-hover:translate-x-1" />
                  </span>
                </div>
              </Link>
            );
          })}
        </div>

        {community ? <IdeaBlock communityId={community.id} allowed={["WALK"]} /> : null}
      </div>
    </div>
  );
}
